// Pusher connection-health signals, apart from the connection manager on purpose: RUM
// registers its handler at boot, and importing it from pusher-connection.ts would pull
// pusher-js into the boot download, which the hooks now load lazily (load-pusher-connection.ts).
// No imports.

/**
 * A Pusher connection-health signal. `kind` is the sub-kind
 * (`connected` | `disconnected` | `unavailable` | `reconnecting` |
 * `subscription_error` | `idle_disconnect` | `reopen` | `refcount_underflow` | `load_error`);
 * `detail` is optional free context (a channel name, a timing). RUM consumes these
 * as client errors (kind="pusher").
 */
export interface PusherHealthEvent {
  kind: string;
  detail?: string;
}

// Inverted dependency: the pusher manager NEVER imports rum. Instead rum (or any
// consumer) registers a callback here, and the health sites emit into it.
// This keeps pusher/ app-agnostic — it emits health signals whether or not anyone
// is listening. One handler process-wide (module singleton), like the connections.
let pusherHealthHandler: ((event: PusherHealthEvent) => void) | null = null;

/**
 * Register (or clear, with `null`) the consumer of Pusher connection-health
 * signals. The app's `initRum` wires this to RUM's client-error queue; nothing
 * else should. Absent a handler, `emitPusherHealth` is a silent no-op.
 */
export function setPusherHealthHandler(handler: ((event: PusherHealthEvent) => void) | null): void {
  pusherHealthHandler = handler;
}

/** Emit a health signal to the registered consumer (no-op when none). INTERNAL. */
export function emitPusherHealth(event: PusherHealthEvent): void {
  pusherHealthHandler?.(event);
}
